import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import QuizTypes from 'types/QuizTypes';
import MediaFormats from 'types/MediaFormats';
// Components
import { Input, Toggle } from 'components/Controls';
import ImageUpload from 'components/ImageUpload';
import MultiLanguageTextField from 'components/MultiLanguageTextField';

const CallToActionForm:FC = () => {
  const { t } = useTranslation();

  const { control, watch } = useFormContext();

  const quizType = watch('type');
  const ctaEnabled = watch('callToAction.enabled');

  const isSurveyQuiz = quizType === QuizTypes.Survey;
  const isPollQuizzes = [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quizType);
  const isSurveyOrPollQuizzes = isSurveyQuiz || isPollQuizzes;
  const isScoringQuizzes = [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quizType);

  return (
    <div className="w-full">
      {quizType !== QuizTypes.Survey ? (
        <Fragment>
          <div className="flex items-center mb-8">
            <span className="block text-white text-base font-semibold mr-3">
              {isSurveyOrPollQuizzes ? t('pages.quizForm.postQuizPageLabel') : t('pages.quizForm.postQuizCallToActionLabel')}
            </span>
            <Controller
              control={control} name="callToAction.enabled"
              render={({ field: { onChange, value } }) => (
                <Toggle
                  id="proceedEnabled"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <span className="block text-gray-400 text-sm font-light leading-5 mb-4">
            {isSurveyOrPollQuizzes ? t('pages.quizForm.callToActionPollHint') : t('pages.quizForm.callToActionHint')}
          </span>
        </Fragment>
      ) : null}

      <div className="flex flex-wrap -mx-2">
        <div className="w-full laptop:w-1/2 px-2">
          <MultiLanguageTextField
            name="callToAction.title"
            label={t('labels.title')}
            disabled={!ctaEnabled}
            className="mb-4"
          />
          {!isSurveyQuiz ? (
            <MultiLanguageTextField
              name="callToAction.resultButtonTitle"
              label={isPollQuizzes ? t('pages.quizForm.resultButtonTitle') : t('pages.quizForm.leaderboardButtonTitle')}
              helperText={isPollQuizzes ? t('pages.quizForm.callToActionButtonTitlePollHint') : t('pages.quizForm.callToActionButtonTitleHint')}
              disabled={!ctaEnabled}
              className="mb-4"
            />
          ) : null}
          {isSurveyOrPollQuizzes || isScoringQuizzes ? (
            <Fragment>
              <Controller
                control={control} name="callToAction.url"
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('pages.quizForm.callToActionUrlLabel')} id="proceedUrl" name="proceedUrl"
                    helperText={t('pages.quizForm.callToActionUrlHint')}
                    disabled={!ctaEnabled}
                  />
                )}
              />
              <Controller
                control={control} name="callToAction.url2"
                render={({ field }) => (
                  <Input
                    {...field}
                    label={`${t('pages.quizForm.callToActionUrlLabel')} 2`} id="proceedUrl2" name="proceedUrl2"
                    helperText={t('pages.quizForm.callToActionUrlHint')}
                    disabled={!ctaEnabled}
                  />
                )}
              />
              <MultiLanguageTextField
                name="callToAction.text"
                label={t('pages.quizForm.callToActionTextLabel')}
                helperText={t('pages.quizForm.callToActionTexthint')}
                disabled={!ctaEnabled}
                className="mb-4"
              />
              <MultiLanguageTextField
                name="callToAction.text2"
                label={`${t('pages.quizForm.callToActionTextLabel')} 2`}
                helperText={t('pages.quizForm.callToActionTexthint')}
                disabled={!ctaEnabled}
                className="mb-4"
              />
            </Fragment>
          ) : null}
          {isScoringQuizzes ? (
            <Controller
              control={control} name="callToAction.clickBonusPoints"
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('pages.quizForm.callToActionClickBonusPointsLabel')} id="proceedClickBonusPoints" name="proceedClickBonusPoints"
                  helperText={t('pages.quizForm.callToActionClickBonusPointsHint')}
                  disabled={!ctaEnabled}
                />
              )}
            />
          ) : null}
        </div>
        <div className={`w-full ${isSurveyOrPollQuizzes ? 'laptop:w-1/3' : 'laptop:w-1/2'} px-2`}>
          <Controller
            control={control} name="callToAction.image"
            render={({ field }) => (
              <ImageUpload
                {...field}
                format={MediaFormats.CTA}
                label="pages.quizForm.callToActionImageLabel"
                disabled={!ctaEnabled}
              />
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default CallToActionForm;
